import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CardContainer } from '../components/ui/CardContainer';
import { FormInput } from '../components/ui/FormInput';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { EMAIL_REGEX } from '../constants/validation';
import { env } from '../config/env';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { AuthLayout } from '../components/layout/AuthLayout';

type ForgotPasswordForm = {
  email: string;
};

export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${env.VITE_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        setError(t('auth.forgot_password.error.connection_error'));
      }
    } catch {
      setError(t('auth.forgot_password.error.connection_error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthLayout>
        <CardContainer>
          <div className="text-center">
            <CheckCircleIcon size={64} className="mx-auto mb-4 text-green-500" weight="fill" />
            <p className="text-secondary mb-6">{t('auth.forgot_password.success.description')}</p>
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t('auth.forgot_password.cta.go_to_login')}
            </Link>
          </div>
        </CardContainer>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <CardContainer>
        <h1 className="text-3xl font-display text-primary mb-2 text-center">
          {t('auth.forgot_password.title')}
        </h1>
        <p className="text-secondary text-center mb-8">{t('auth.forgot_password.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <FormInput
            label={t('auth.forgot_password.email.label')}
            type="email"
            placeholder={t('auth.forgot_password.email.placeholder')}
            error={errors.email?.message}
            required
            {...register('email', {
              required: t('auth.forgot_password.email.error.required'),
              pattern: {
                value: EMAIL_REGEX,
                message: t('auth.forgot_password.email.error.invalid'),
              },
            })}
          />

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <PrimaryButton type="submit" loading={isLoading}>
            {t('auth.forgot_password.cta.submit')}
          </PrimaryButton>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline">
            {t('auth.forgot_password.cta.go_to_login')}
          </Link>
        </div>
      </CardContainer>
    </AuthLayout>
  );
};
